/**
 * Core API - Customers
 *
 * Database operations for external customers (e.g., BMW, Mercedes, etc.).
 * Customers are factory-scoped and represent organizations purchasing products.
 */

import { supabase } from '@/integrations/supabase/client';

// =============================================================================
// Types
// =============================================================================

export interface Customer {
  id: string;
  tenant_id: string;
  factory_id: string;
  name: string;
  description: string | null;
  organization_code: string | null;
  internal_code: string | null;
  contact_email: string | null;
  customer_since: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateCustomerInput {
  tenant_id: string;
  factory_id: string;
  name: string;
  description?: string | null;
  organization_code?: string | null;
  internal_code?: string | null;
  contact_email?: string | null;
  customer_since?: string | null;
}

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetches all active customers for a factory, ordered by name.
 */
export async function fetchCustomersByFactory(factoryId: string): Promise<Customer[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('customers')
    .select('*')
    .eq('factory_id', factoryId)
    .eq('is_active', true)
    .order('name', { ascending: true });

  if (error) throw error;
  return data;
}

/**
 * Searches customers by name, internal code, or organization code.
 * Optimized for large datasets (100k+ records) - uses server-side filtering.
 */
export async function searchCustomers(
  factoryId: string,
  searchTerm: string
): Promise<Customer[]> {
  // Sanitize search term to prevent SQL injection via ilike
  const sanitized = searchTerm.replace(/[%_]/g, '\\$&');
  
  const { data, error } = await supabase
    .schema('core')
    .from('customers')
    .select('*')
    .eq('factory_id', factoryId)
    .eq('is_active', true)
    .or(`name.ilike.%${sanitized}%,internal_code.ilike.%${sanitized}%,organization_code.ilike.%${sanitized}%`)
    .order('name', { ascending: true })
    .limit(50);

  if (error) throw error;
  return data;
}

/**
 * Fetches a single customer by ID.
 */
export async function fetchCustomerById(customerId: string): Promise<Customer | null> {
  const { data, error } = await supabase
    .schema('core')
    .from('customers')
    .select('*')
    .eq('id', customerId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/**
 * Fetches customers with their complaint count (for dashboard stats).
 */
export async function fetchCustomersWithComplaintCount(
  factoryId: string
): Promise<Array<Customer & { complaint_count: number }>> {
  const { data, error } = await supabase
    .schema('core')
    .from('customers')
    .select(`
      *,
      complaint_count:customer_complaints(count)
    `)
    .eq('factory_id', factoryId)
    .eq('is_active', true)
    .order('name', { ascending: true });

  if (error) throw error;
  return data as unknown as Array<Customer & { complaint_count: number }>;
}

// =============================================================================
// Mutations
// =============================================================================

/**
 * Creates a new customer.
 */
export async function createCustomer(input: CreateCustomerInput): Promise<Customer> {
  const { data, error } = await supabase
    .schema('core')
    .from('customers')
    .insert(input)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Updates an existing customer.
 */
export async function updateCustomer(
  customerId: string,
  updates: Partial<Omit<CreateCustomerInput, 'tenant_id' | 'factory_id'>>
): Promise<Customer> {
  const { data, error } = await supabase
    .schema('core')
    .from('customers')
    .update(updates)
    .eq('id', customerId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Soft-deletes a customer (sets is_active = false).
 */
export async function deleteCustomer(customerId: string): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('customers')
    .update({ is_active: false })
    .eq('id', customerId);

  if (error) throw error;
}
